import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    Validators,
    ReactiveFormsModule
} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {finalize} from 'rxjs/operators';
import {CompanyInfo} from '@/common/Enum/SettingsType.enum';
import {CompanyService} from '@services/company.service';

@Component({
    selector: 'app-company-information',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './company-information.component.html',
    styleUrl: './company-information.component.scss'
})
export class CompanyInformationComponent implements OnInit {
    public companyForm: FormGroup;
    public isLoading: boolean = false;
    public isEditMode: boolean = false;
    public companyInformation: CompanyInfo | null = null;
    private userId: number | null = null;
    private UserDetail;
    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private companyService: CompanyService
    ) {}

    ngOnInit(): void {
        const userDetailString = localStorage.getItem('UserDetail');
        this.UserDetail = userDetailString
            ? JSON.parse(userDetailString)
            : null;
        this.userId = this.UserDetail?.id;
        this.initializeForm();
        this.loadCompanyData();
    }

    initializeForm(): void {
        this.companyForm = this.fb.group({
            companyName: [
                {value: '', disabled: true},
                [Validators.required, Validators.minLength(2)]
            ],
            address: [
                {value: '', disabled: true},
                [Validators.required, Validators.minLength(5)]
            ],
            city: [
                {value: '', disabled: true},
                [Validators.required, Validators.minLength(2)]
            ],
            country: [
                {value: '', disabled: true},
                [Validators.required, Validators.minLength(2)]
            ],
            phoneNumber: [
                {value: '', disabled: true},
                [Validators.required, Validators.pattern(/^[0-9]{10}$/)]
            ],
            emailAddress: [
                {value: '', disabled: true},
                [
                    Validators.required,
                    Validators.pattern(
                        '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
                    )
                ]
            ],
            website: [{value: '', disabled: true}]
        });
    }

    loadCompanyData(): void {
        if (!this.userId) {
            this.toastr.error('User ID not found. Please log in again.');
            return;
        }
        this.companyService.getCompanyInformation(this.userId).subscribe({
            next: (result) => {
                this.companyInformation = result;
                if (this.companyForm) {
                    this.companyForm.patchValue({
                        companyName: result.companyName || '',
                        address: result.address || '',
                        city: result.city || '',
                        country: result.country || '',
                        phoneNumber: result.phoneNumber || '',
                        emailAddress: result.emailAddress || '',
                        website: result.website || ''
                    });
                }
            },
            error: (err) => {
                this.toastr.error('Failed to load company information');
            }
        });
    }

    toggleEditMode(): void {
        this.isEditMode = !this.isEditMode;

        if (this.isEditMode) {
            Object.keys(this.companyForm.controls).forEach((key) => {
                this.companyForm.get(key)?.enable();
            });
        } else {
            Object.keys(this.companyForm.controls).forEach((key) => {
                this.companyForm.get(key)?.disable();
            });
            this.loadCompanyData();
        }
    }

    onUpdate(): void {
        if (this.companyForm.invalid) {
            this.companyForm.markAllAsTouched();
            this.toastr.error('Please fill all required fields correctly');
            return;
        }

        this.isLoading = true;

        const formValues = this.companyForm.getRawValue();
        const payload: CompanyInfo = {
            id: this.companyInformation?.id,
            userId: this.userId || undefined,
            companyName: formValues.companyName,
            address: formValues.address,
            city: formValues.city,
            country: formValues.country,
            phoneNumber: formValues.phoneNumber,
            emailAddress: formValues.emailAddress,
            website: formValues.website,
            createdOnUtc: this.companyInformation?.createdOnUtc,
            updatedOnUtc: new Date().toISOString()
        };

        this.companyService
            .updateCompanyInformation(payload)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe({
                next: () => {
                    this.toastr.success(
                        'Company information updated successfully'
                    );
                    this.isEditMode = false;
                    Object.keys(this.companyForm.controls).forEach((key) => {
                        this.companyForm.get(key)?.disable();
                    });
                },
                error: () => {
                    this.toastr.error('Failed to update company information');
                }
            });
    }

    onCancel(): void {
        this.toggleEditMode();
        this.toastr.info('Changes cancelled');
    }

    get formControls() {
        return this.companyForm.controls;
    }
}
